import { ChangeEvent, CSSProperties, FC, memo, useEffect, useMemo, useRef, useState } from 'react'

import * as S from './RangeSlider.styles'

export interface RangeSliderOutput {
  min: number
  max: number
}

interface RangeSliderProps {
  className?: string
  style?: CSSProperties
  minRange?: number
  maxRange?: number
  defaultValue?: RangeSliderOutput
  onChange: ({ min, max }: RangeSliderOutput) => void
  tooltipFormatter?: (value: number) => string
  step?: number
  disabled?: boolean
}

export const RangeSlider: FC<RangeSliderProps> = memo(
  ({
    className,
    style,
    minRange = 0,
    maxRange = 100,
    defaultValue,
    onChange,
    step,
    disabled,
    tooltipFormatter,
  }) => {
    const [valueA, setValueA] = useState(defaultValue?.min ?? minRange)
    const [valueB, setValueB] = useState(defaultValue?.max ?? maxRange)

    const rangeSelected = useMemo(() => {
      return {
        min: Math.min(valueA, valueB),
        max: Math.max(valueA, valueB),
      }
    }, [valueA, valueB])

    const lastRangeTriggered = useRef<RangeSliderOutput>(rangeSelected)

    useEffect(() => {
      if (defaultValue?.min !== undefined && defaultValue?.max !== undefined) {
        setValueA(defaultValue.min)
        setValueB(defaultValue.max)
      }
    }, [defaultValue?.min, defaultValue?.max])

    const calculatePosition = (value: number) => {
      return ((value - minRange) / (maxRange - minRange)) * 100
    }

    const start = Math.min(calculatePosition(valueA), calculatePosition(valueB))

    const diff = Math.min(
      Math.abs(calculatePosition(valueA) - calculatePosition(valueB)),
      100 - start,
    )

    const handleChangeA = (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(event.target.value)
      setValueA(newValue)
    }

    const handleChangeB = (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(event.target.value, 10)
      setValueB(newValue)
    }

    const handlePointerUp = () => {
      const { min, max } = lastRangeTriggered.current
      if (rangeSelected.min !== min || rangeSelected.max !== max) {
        lastRangeTriggered.current = rangeSelected
        onChange(rangeSelected)
      }
    }

    return (
      <S.Root className={className} style={style} $disabled={disabled}>
        <S.SliderContainer>
          <S.Track />
          <S.Track active left={`${start}%`} width={`${diff}%`} />
          <S.ThumbsWrapper>
            <S.Thumb position={`${calculatePosition(valueA)}%`}>
              <S.ThumbValue>{tooltipFormatter ? tooltipFormatter(valueA) : valueA}</S.ThumbValue>
            </S.Thumb>
            <S.Thumb position={`${calculatePosition(valueB)}%`}>
              <S.ThumbValue>{tooltipFormatter ? tooltipFormatter(valueB) : valueB}</S.ThumbValue>
            </S.Thumb>
          </S.ThumbsWrapper>
          <S.StyledRangeInput
            disabled={disabled}
            min={minRange}
            max={maxRange}
            step={step ?? 1}
            value={valueA}
            onChange={handleChangeA}
            onPointerUp={handlePointerUp}
          />
          <S.StyledRangeInput
            disabled={disabled}
            min={minRange}
            max={maxRange}
            step={step ?? 1}
            value={valueB}
            onChange={handleChangeB}
            onPointerUp={handlePointerUp}
          />
        </S.SliderContainer>
      </S.Root>
    )
  },
)
